import type { Price } from "@/catalog/types/prices";
import type { FilterCondition } from "@/shared/types/filters";

// Values the API accepts in product create/update payloads
export type ProductPayloadValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | Record<string, string>
  | Record<string, string | number | boolean | null>
  | ProductIdentifier[]
  | ProductPricePayload[];

export interface ProductIdentifier {
  type: string;
  value: string;
  [key: string]: string;
}

export interface ProductPricePayload {
  id?: string;
  currency?: string;
  unit_amount?: number;
  unit_amount_decimal?: string;
  nickname?: string;
  metadata?: Record<string, string>;
  isDefault?: boolean;
  tax_behavior?: string;
  [key: string]: string | number | boolean | Record<string, string> | undefined;
}

export type ProductPayload = Record<string, ProductPayloadValue>;

// Client-side product filters are assembled by several components and serialized into query strings.
export type ProductFilterCondition = FilterCondition;

export interface Product {
  id: string;
  object: string;
  active: boolean | string;
  attributes?: Record<string, string>;
  created: number | string;
  default_price?: string | Price | number | null;
  description: string;
  features?: unknown[];
  images: string[];
  livemode: boolean;
  metadata?: Record<string, string>;
  name: string;
  sort?: number | null;
  package_dimensions?: Record<string, number> | null;
  shippable?: boolean | null;
  statement_descriptor?: string | null;
  tax_code?: string | null;
  tax_reference?: string;
  type?: string;
  unit_label?: string | null;
  updated: number;
  url: string;
  prices?: Record<string, Price> | Price[];
  tags?: import("@/catalog/types/productTags").ProductTagItem[];
  menuOpen: boolean;
  stockCount?: number;
  hasInventory?: boolean;
  canOversell?: boolean;
  inventoryLowerBound?: number | null;
  lowStockThreshold?: number | null;
  sku?: string;
  identifiers?: ProductIdentifier[] | Record<string, string>;
  assignedLocationIds?: string[] | null;
  product_class?: ProductClassRef | null;
  // Present on products returned by the barcode scan endpoint
  locationId?: string;
  additional_prices?: Price[];
}

export interface ProductClassRef {
  id?: string;
  name?: string;
  description?: string;
  images?: string[];
  tags?: import("@/catalog/types/productTags").ProductTagItem[];
}

/**
 * Convert a catalog Product into the loose snapshot shape consumed by the cart.
 * The cart tolerates partial data; we coerce the default_price union into its
 * object form so the snapshot is structurally compatible.
 */
export const toCartProductSnapshot = (
  product: Product,
): import("@/cart/types/cart").CartProductSnapshot => {
  const defaultPriceObject =
    typeof product.default_price === "object" && product.default_price !== null
      ? product.default_price
      : undefined;

  return {
    ...product,
    lowStockThreshold: product.lowStockThreshold ?? undefined,
    default_price: defaultPriceObject
      ? {
          id: defaultPriceObject.id,
          nickname: defaultPriceObject.nickname,
          unit_amount: defaultPriceObject.unit_amount,
          currency: defaultPriceObject.currency,
          sku: defaultPriceObject.sku,
        }
      : null,
  };
};

export interface ProductsStore {
  products: Product[];
  selectedProduct: Product | null;
  images: string[];
  loading: boolean;
  filterProductCondition: ProductFilterCondition;
  routeFilterMap: Record<string, ProductFilterCondition>;
  listLoading: boolean;
  noResults: boolean;
  hasMore: boolean;
  hasNext: boolean;
  hasPrev: boolean;
  nextPage: string;
  productPayload: ProductPayload;
}

export interface ProductSearchPagination {
  limit?: number;
  nextPage?: string;
  prevPage?: string;
  page?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface ProductSearchOptions {
  pagination?: ProductSearchPagination;
  filters?: ProductFilterCondition;
  includeStoredFilters?: boolean;
  updateStore?: boolean;
}

export interface AdjustInventoryResponse {
  stockCount: number;
  [key: string]: string | number | boolean | null | undefined;
}

export interface AdjustInventoryLocationResponse {
  productId: string;
  locations: Array<{ locationId: string; stockCount: number }>;
}

export type ProductAdjustInventoryResponse =
  | AdjustInventoryResponse
  | AdjustInventoryLocationResponse;

export interface PaginatedProductsResponse {
  data: Product[];
  has_more?: boolean;
  hasNext?: boolean;
  hasPrev?: boolean;
  next_page?: string;
}
